/**
 * K108-R Batch 4 — Monitored training session history list.
 *
 * Shows the technician's LAUNCHED/monitored training sessions (not only manually
 * completed items) with OptiBay's observed engagement (Batch 2) and the user's
 * report (Batch 3 attestation), side by side and honestly. Degrades gracefully
 * when extension evidence is missing (Safari 403 / no Companion): shows
 * "OptiBay observed: No monitored evidence available" and still renders the
 * session + any user report. Never throws, never blocks.
 */
import { useMemo } from 'react';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Eye, Ticket, GraduationCap, Play } from 'lucide-react';
import { useTrainingCompletionReturnPrompt } from '@/contexts/TrainingCompletionReturnPromptProvider';
// K108-Y — resume launches through the SAME Companion monitored flow.
import { useOpenTrainingWithSession } from '@/hooks/useOpenTrainingWithSession';
import { useCompanionReadiness } from '@/hooks/useCompanionReadiness';
import { approvedDomainsForUrl } from '@/lib/trainingCompanion/approvedProviderDomains';
import { writeMobileTrainingLaunch } from '@/lib/training/mobileTrainingLaunchState';
import { clock } from '@/utils/clock';
import { TrainingQuizModal } from '@/components/training/TrainingQuizModal';
import { useTrainingQuizLauncher } from '@/hooks/useTrainingQuizLauncher';
import type { TrainingHistoryCard } from '@/lib/training/trainingHistoryCard';
import { useTrainingSessionHistory } from '@/hooks/useTrainingSessionHistory';
import { useTrainingSessionMonitoringSummary } from '@/hooks/useTrainingSessionMonitoringSummary';
import {
  buildTrainingHistoryCard,
  sortHistoryCards,
  type HistoryStatus,
} from '@/lib/training/trainingHistoryCard';

const STATUS_VARIANT: Record<HistoryStatus, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  Completed: 'default',
  'User-reported complete': 'secondary',
  'Partially completed': 'secondary',
  'In progress': 'outline',
  'Needs review': 'destructive',
  'Reviewed – report accepted': 'default',
  'Reviewed – partial': 'secondary',
  'Follow-up requested': 'secondary',
  'Reviewed – no action': 'outline',
  'Skipped / not relevant': 'outline',
  Superseded: 'outline',
  'Evidence unavailable': 'outline',
};

function fmt(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : format(d, 'MMM d, yyyy h:mm a');
}

export function MonitoredTrainingHistoryList({
  technicianProfileId,
}: {
  technicianProfileId?: string | null;
}) {
  const returnPrompt = useTrainingCompletionReturnPrompt();
  const { openTraining } = useOpenTrainingWithSession();
  const companion = useCompanionReadiness({ enabled: true });
  const { data: sessions } = useTrainingSessionHistory(technicianProfileId);
  // Phase Q1 — post-completion quiz. OFF until the quiz backend is live.
  // The launcher gates on the flag + an approved-quiz pre-check and refreshes
  // this list after an attempt so the badge/button update.
  const quiz = useTrainingQuizLauncher();

  // K108-Y: resume the SAME pending session (existingSessionId → no new History row;
  // resume:true on the mint rotates the token). Goes through the monitored launch
  // (per-launch disclosure + cached durable consent + Companion handoff), not a
  // plain link.
  // A mobile record resumes through the MOBILE flow: re-open the real URL and
  // restart the return-timer, carrying the SAME session id so no duplicate row
  // is created and the return prompt updates this record. It never marks
  // complete and never mints Companion evidence.
  const resumeMobileTraining = (card: TrainingHistoryCard) => {
    if (!card.resume) return;
    writeMobileTrainingLaunch({
      artifactId: card.resume.artifactId,
      ticketId: card.resume.ticketId,
      technicianProfileId: technicianProfileId ?? null,
      providerUrl: card.resume.targetUrl,
      durationMinutes: card.resume.expectedDurationMinutes,
      launchedAtMs: clock.nowMs(),
      existingSessionId: card.resume.sessionId,
    });
    window.open(card.resume.targetUrl, '_blank', 'noopener,noreferrer');
  };

  const resumeTraining = (card: TrainingHistoryCard) => {
    if (!card.resume || !technicianProfileId) return;
    if (card.mobile) {
      resumeMobileTraining(card);
      return;
    }
    openTraining({
      existingSessionId: card.resume.sessionId,
      technicianProfileId,
      artifactId: card.resume.artifactId,
      artifactType: card.resume.artifactType,
      expectedDurationMinutes: card.resume.expectedDurationMinutes,
      targetUrl: card.resume.targetUrl,
      ticketId: card.resume.ticketId ?? undefined,
      surface: 'foundational',
      source: 'learning_center',
      launchSource: 'learning_library',
      delegateProviderTabToCompanion: companion.state === 'installed',
      approvedDomains: approvedDomainsForUrl(card.resume.targetUrl),
    });
  };

  // Re-open a COMPLETED artifact (re-watch). Fresh launch (no existingSessionId)
  // so the prior completion status is untouched.
  const reopenTraining = (card: TrainingHistoryCard) => {
    if (!card.reopen || !technicianProfileId) return;
    openTraining({
      technicianProfileId,
      artifactId: card.reopen.artifactId,
      artifactType: card.reopen.artifactType,
      expectedDurationMinutes: card.reopen.expectedDurationMinutes,
      targetUrl: card.reopen.targetUrl,
      ticketId: card.reopen.ticketId ?? undefined,
      surface: 'foundational',
      source: 'learning_center',
      launchSource: 'learning_library',
      delegateProviderTabToCompanion: companion.state === 'installed',
      approvedDomains: approvedDomainsForUrl(card.reopen.targetUrl),
    });
  };
  const sessionIds = useMemo(() => (sessions ?? []).map((s) => s.id), [sessions]);
  const { data: summaries } = useTrainingSessionMonitoringSummary({
    sessionIds,
    enabled: sessionIds.length > 0,
  });

  const cards = useMemo(() => {
    const built = (sessions ?? []).map((s) =>
      buildTrainingHistoryCard(s, summaries?.get(s.id) ?? null),
    );
    return sortHistoryCards(built);
  }, [sessions, summaries]);

  if (!sessions || sessions.length === 0) return null;

  return (
    <div className="space-y-2" data-testid="monitored-training-history">
      <h4 className="text-xs font-semibold uppercase text-muted-foreground">
        Launched &amp; monitored training
      </h4>

      {/* K108-W: persistent CTA to attest a pending monitored session (the app-level
          provider also auto-opens the modal on return to OptiBay). */}
      {returnPrompt?.candidate && (
        <div
          data-testid="history-record-completion-cta"
          className="flex items-center justify-between gap-3 rounded-lg border border-primary/30 bg-primary/5 p-3"
        >
          <div className="flex items-center gap-2 min-w-0">
            <GraduationCap className="h-4 w-4 flex-shrink-0 text-primary" />
            <span className="text-sm truncate">
              Record training completion for{' '}
              <span className="font-medium">{returnPrompt.candidate.session.artifact.title}</span>
            </span>
          </div>
          <Button size="sm" onClick={() => returnPrompt.openPrompt()} className="flex-shrink-0">
            Record completion
          </Button>
        </div>
      )}
      {cards.map((c) => (
        <div
          key={c.sessionId}
          data-testid="monitored-history-card"
          className="rounded-lg border bg-card p-3 hover:bg-accent/50 transition-colors"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-medium truncate">{c.title}</p>
              {c.provider && <p className="text-xs text-muted-foreground">{c.provider}</p>}
              {c.retired && (
                <p className="text-xs text-muted-foreground" data-testid="monitored-history-retired">
                  Retired — no longer offered. Your record of it is kept.
                </p>
              )}
            </div>
            <Badge variant={STATUS_VARIANT[c.status]} className="flex-shrink-0 text-xs">
              {c.status}
            </Badge>
          </div>

          <div className="mt-2 space-y-0.5 text-xs text-muted-foreground">
            <p className="flex items-center gap-1">
              <Eye className="h-3 w-3 flex-shrink-0" />
              {c.observedLine}
              {c.confidence && !c.evidenceUnavailable ? ` · ${c.confidence} engagement` : ''}
            </p>
            {c.reportedLine && <p>{c.reportedLine}</p>}
            {c.mobileNote && (
              <p className="text-foreground" data-testid="monitored-history-mobile-note">
                {c.mobileNote}
              </p>
            )}
            {c.quizLabel && (
              <p className="text-foreground" data-testid="monitored-history-quiz-label">
                {c.quizLabel}
                {c.quiz?.scorePct != null ? ` (${c.quiz.scorePct}%)` : ''} · Not Companion verified
              </p>
            )}
            {c.ticketDisplay ? (
              <p className="flex items-center gap-1">
                <Ticket className="h-3 w-3 flex-shrink-0" />
                Linked ticket: #{c.ticketDisplay}
              </p>
            ) : c.ticketId ? (
              // Never render the raw UUID — neutral label when no readable number.
              <p className="flex items-center gap-1">
                <Ticket className="h-3 w-3 flex-shrink-0" />
                Linked ticket available
              </p>
            ) : null}
            {c.lastActivityAt && <p>Last activity: {fmt(c.lastActivityAt)}</p>}
            {c.mismatchNote && !c.reviewedLine && (
              <p className="text-amber-700" data-testid="monitored-history-mismatch">
                {c.mismatchNote}
              </p>
            )}
            {c.reviewedLine && (
              <p className="text-foreground" data-testid="monitored-history-reviewed">
                {c.reviewedLine}
              </p>
            )}
          </div>

          {/* K108-Y: Resume only while the session is still pending. Disabled with a
              neutral note when the artifact link is unavailable. */}
          {c.resumable && (
            <div className="mt-2" data-testid="monitored-history-resume">
              {c.resume ? (
                <>
                  <Button size="sm" variant="outline" className="gap-1" onClick={() => resumeTraining(c)}>
                    <Play className="h-3 w-3" />
                    Resume training
                  </Button>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {c.mobile
                      ? 'Reopen on this device — OptiBay will ask you to confirm when you return.'
                      : 'Continue this training with Companion monitoring.'}
                  </p>
                </>
              ) : (
                <p className="text-xs text-muted-foreground" data-testid="monitored-history-resume-unavailable">
                  Resume unavailable — the training link is not available for this session.
                </p>
              )}
            </div>
          )}

          {/* Completed artifacts stay re-openable (re-watch) — a fresh launch that
              never changes the recorded completion. Companion not required. */}
          {c.reopen && (
            <div className="mt-2" data-testid="monitored-history-reopen">
              <Button size="sm" variant="outline" className="gap-1" onClick={() => reopenTraining(c)}>
                <Play className="h-3 w-3" />
                Re-open training
              </Button>
            </div>
          )}

          {/* Phase Q1 — optional knowledge-check quiz on a completed row. Flag-gated
              (OFF until the quiz backend is live). Never blocks; passing is a
              knowledge signal only, never Companion/provider verification. */}
          {quiz.quizEnabled &&
            c.artifactId &&
            (c.status === 'User-reported complete' || c.status === 'Completed') &&
            (c.quiz === null || c.quiz.status === 'not_passed') && (
              <div className="mt-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => void quiz.launch(c.artifactId, c.sessionId)}
                  data-testid="monitored-history-take-quiz"
                >
                  {c.quiz?.status === 'not_passed' ? 'Retake quiz' : 'Take quiz'}
                </Button>
              </div>
            )}
        </div>
      ))}

      {quiz.target && (
        <TrainingQuizModal
          open
          artifactId={quiz.target.artifactId}
          sessionId={quiz.target.sessionId}
          adapter={quiz.adapter}
          prefetchedQuiz={quiz.target.quiz}
          onClose={quiz.close}
          onResult={quiz.handleResult}
        />
      )}
    </div>
  );
}
